import {Box, Grid, TextField, Typography} from '@mui/material';
import PropTypes from 'prop-types';
import React, {useCallback, useState} from 'react';
import type {ClipboardEvent} from 'react';
import {useFormContext, useWatch} from 'react-hook-form';
import Errors from 'src/modules/shared/error/errors';
import {
  getClipboardImageFiles,
  uploadAndAppendImagesFromClipboard,
} from 'src/view/shared/form/clipboardImageFormUpload';
import FormErrors from 'src/view/shared/form/formErrors';

function TextAreaFormItem(props) {
  const {
    label,
    name,
    hint,
    placeholder,
    autoFocus,
    autoComplete,
    externalErrorMessage,
    required,
    defaultValue,
    rows,
    disabled,
    onChange,
    onBlur,
    pasteUploadedImagesField,
    pasteUploadedImagesStorage,
    pasteUploadedImagesMax,
  } = props;

  const {getFieldState, register, formState, control, setValue, getValues} = useFormContext();
  const {touchedFields, isSubmitted, errors} = formState;
  const {isTouched, error} = getFieldState(name, formState);

  const watchName: any = useWatch({name, control, defaultValue});
  const [uploadingPastedImages, setUploadingPastedImages] = useState(false);

  const message = error?.message;
  const errorMessage =
    isTouched && message ? message : FormErrors.errorMessage(name, errors, touchedFields, isSubmitted, externalErrorMessage);

  const handlePasteImages = useCallback(
    async (e: ClipboardEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      if (!pasteUploadedImagesField || !pasteUploadedImagesStorage) {
        return;
      }
      const raw = getClipboardImageFiles(e);
      if (!raw.length) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      try {
        setUploadingPastedImages(true);
        const current = getValues(pasteUploadedImagesField);
        const next = await uploadAndAppendImagesFromClipboard(
          raw,
          pasteUploadedImagesStorage,
          Array.isArray(current) ? current : [],
          pasteUploadedImagesMax,
        );
        setValue(pasteUploadedImagesField, next, {shouldValidate: true, shouldDirty: true});
      } catch (err) {
        console.error(err);
        Errors.showMessage(err);
      } finally {
        setUploadingPastedImages(false);
      }
    },
    [
      pasteUploadedImagesField,
      pasteUploadedImagesStorage,
      pasteUploadedImagesMax,
      getValues,
      setValue,
    ],
  );

  if (disabled) {
    return (
      <Grid mb={1} container direction="column">
        {label && <Typography variant="h4">{label}</Typography>}
        <Box>
          {watchName ? (
            <Typography
              component="pre"
              variant="subtitle2"
              sx={{
                fontWeight: 500,
                fontSize: '1.15em',
                fontFamily: 'inherit',
                whiteSpace: 'pre-wrap',
              }}>
              {watchName}
            </Typography>
          ) : (
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 500,
                fontSize: '1.05em',
              }}>
              Sin Respuesta
            </Typography>
          )}
        </Box>
      </Grid>
    );
  }

  const inputRef: any = register(name, {required, onChange: (event) => onChange && onChange(event.target.value), onBlur});

  return (
    <TextField
      {...inputRef}
      inputRef={inputRef.ref}
      disabled={disabled || uploadingPastedImages}
      defaultValue={defaultValue}
      id={name}
      name={name}
      label={label}
      required={required}
      margin={props.margin || 'normal'}
      fullWidth
      variant="outlined"
      size="small"
      style={props.style}
      placeholder={placeholder || undefined}
      autoFocus={autoFocus || undefined}
      autoComplete={autoComplete || undefined}
      InputLabelProps={{
        shrink: true,
      }}
      multiline
      rows={rows}
      error={Boolean(errorMessage)}
      helperText={errorMessage || hint}
      inputProps={{
        onPaste: handlePasteImages,
      }}
    />
  );
}

TextAreaFormItem.defaultProps = {
  type: 'text',
  required: false,
};

TextAreaFormItem.propTypes = {
  name: PropTypes.string.isRequired,
  required: PropTypes.bool,
  label: PropTypes.string,
  hint: PropTypes.string,
  autoFocus: PropTypes.bool,
  prefix: PropTypes.string,
  placeholder: PropTypes.string,
  externalErrorMessage: PropTypes.string,
  defaultValue: PropTypes.any,
  rows: PropTypes.number,
  disabled: PropTypes.bool,
  noAnswerText: PropTypes.string,
  onChange: PropTypes.func,
  /** Nombre del campo del formulario (array de imágenes) donde acumular pegados desde el portapapeles. */
  pasteUploadedImagesField: PropTypes.string,
  /** Mismo `storage` que el `ImagesFormItem` asociado. */
  pasteUploadedImagesStorage: PropTypes.object,
  /** Opcional; mismo máximo que `ImagesFormItem.max`. */
  pasteUploadedImagesMax: PropTypes.number,
};

export default TextAreaFormItem;
